import React from 'react';

import {cls} from '../../../../components/utils';
import type {Ability} from '../../../../generated/types';
import {useCharacter} from '../character-context';
import {SKILL_ABILITY, SKILLS} from '../model/abilities';
import type {ProficiencyLevel} from '../model/character';
import {abilityLabel, skillLabel} from '../model/labels';
import {RollButton} from './dice-roller';
import {formatBonus} from './format';

import css from './sheet.module.css';

const PROFICIENCY_TITLES: Record<ProficiencyLevel, string | undefined> = {
	none: undefined,
	proficient: 'Умение',
	expertise: 'Экспертность',
};

const ProficiencyMark: React.FC<{level: ProficiencyLevel}> = ({level}) => (
	<span className={cls(css.proficiencyMark, css[level])} title={PROFICIENCY_TITLES[level]} />
);

export const AbilityCard: React.FC<{ability: Ability}> = ({ability}) => {
	const {character} = useCharacter();
	const title = abilityLabel(ability);
	const modifier = character.abilityModifier(ability);
	const saveBonus = character.savingThrowBonus(ability);
	const skills = SKILLS.filter((skill) => SKILL_ABILITY[skill] === ability).sort((first, second) =>
		skillLabel(first).localeCompare(skillLabel(second)),
	);

	return (
		<section className={css.ability}>
			<h3 className={css.abilityName}>{title}</h3>
			<div className={css.abilityValues}>
				<RollButton
					title={`Проверка: ${title}`}
					bonus={character.abilityCheckBonus(ability)}
					context={{kind: 'check', ability}}
					appearance="secondary"
					className={css.abilityModifier}
				>
					{formatBonus(modifier)}
				</RollButton>
				<span className={css.abilityScore} title="Значение">
					{character.abilityScore(ability)}
				</span>
			</div>
			<RollButton
				title={`Испытание: ${title}`}
				bonus={saveBonus}
				context={{kind: 'save', ability}}
				appearance="secondary"
				className={cls(css.abilityRow, css.abilitySave)}
				block
			>
				<ProficiencyMark level={character.isSavingThrowProficient(ability) ? 'proficient' : 'none'} />
				<span className={css.abilityRowName}>Испытание</span>
				<span className={css.abilityRowBonus}>{formatBonus(saveBonus)}</span>
			</RollButton>
			{skills.length > 0 && (
				<ul className={css.abilityRows}>
					{skills.map((skill) => {
						const bonus = character.skillBonus(skill);
						return (
							<li key={skill}>
								<RollButton
									title={skillLabel(skill)}
									bonus={bonus}
									context={{kind: 'check', ability, skill}}
									className={css.abilityRow}
									block
								>
									<ProficiencyMark level={character.skillProficiency(skill)} />
									<span className={css.abilityRowName}>{skillLabel(skill)}</span>
									<span className={css.abilityRowBonus}>{formatBonus(bonus)}</span>
								</RollButton>
							</li>
						);
					})}
				</ul>
			)}
		</section>
	);
};
